import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {useAIChatStream} from '../ai-chat/useAIChatStream';
import {EmptyState} from '../ai-chat/EmptyState';
import {MessageBubble} from '../ai-chat/MessageBubble';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {D} from '../ai-chat/design-tokens';
import DrawerFooter from './DrawerFooter';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import CreateSavedPromptMutation from '../../mutations/agent/CreateSavedPromptMutation';
import {CommitMutation} from '../../mutations/CommitMutation';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';

const MessagesArea = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	background: ${D.bg};
	position: relative;
`;

const ScrollToBottomBtn = styled.button`
	position: absolute;
	bottom: 12px;
	left: 50%;
	transform: translateX(-50%);
	background: ${D.bg};
	border: 1px solid ${D.border};
	border-radius: 9999px;
	padding: 4px 12px;
	font-size: 11px;
	color: ${D.textMuted};
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
	display: flex;
	align-items: center;
	gap: 4px;

	&:hover {
		background: ${D.surface};
	}
`;

const PanelWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`;

const ProvisioningState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: ${D.textMuted};
	font-size: 13px;
`;

type ChatPanelProps = {
	apiKey: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	history: {push: (path: string) => void} | null | undefined;
	isProvisioning?: boolean;
	onRegisterClear?: (clearFn: () => void) => void;
	onRegisterRegenerate?: (regenerateFn: () => void) => void;
	onHasLlmResponseChange?: (hasResponse: boolean) => void;
};

const ChatPanel = ({
	apiKey,
	viewerPersonId,
	companyId,
	companyName,
	history,
	isProvisioning,
	onRegisterClear,
	onRegisterRegenerate,
	onHasLlmResponseChange,
}: ChatPanelProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {inputValue, setInputValue, savedPromptTexts, setSavedPromptTexts} = useAgentDrawerContext();
	const [input, setInput] = useState('');
	const [showScrollBtn, setShowScrollBtn] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (inputValue) {
			setInput(inputValue);
			setInputValue('');
			inputRef.current?.focus();
		}
	}, [inputValue, setInputValue]);

	const handleProjectIntent = useCallback(() => {
		showModal({type: MODAL_TYPE.AI_PROJECT_PLANNING, apiKey, personId: viewerPersonId});
	}, [apiKey, viewerPersonId]);

	const {messages, setMessages, isStreaming, isHitlPending, sendMessage, respondToAction, abort, clearMessages} =
		useAIChatStream({
			apiKey,
			viewerPersonId,
			companyId,
			companyName,
			onProjectIntent: handleProjectIntent,
		});

	const hasLlmResponse = messages.some(m => m.role === 'assistant');

	useEffect(() => {
		onHasLlmResponseChange?.(hasLlmResponse);
	}, [hasLlmResponse, onHasLlmResponseChange]);

	useEffect(() => {
		onRegisterClear?.(() => {
			clearMessages();
			setInput('');
			setShowScrollBtn(false);
		});
	}, [clearMessages, onRegisterClear]);

	// Keep refs so the registered regenerate fn always sees current values without re-registering
	const messagesRef = useRef(messages);
	const isStreamingRef = useRef(isStreaming);
	const sendMessageRef = useRef(sendMessage);
	const setMessagesRef = useRef(setMessages);
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);
	useEffect(() => {
		isStreamingRef.current = isStreaming;
	}, [isStreaming]);
	useEffect(() => {
		sendMessageRef.current = sendMessage;
	}, [sendMessage]);
	useEffect(() => {
		setMessagesRef.current = setMessages;
	}, [setMessages]);

	useEffect(() => {
		onRegisterRegenerate?.(() => {
			if (isStreamingRef.current) return;
			const msgs = messagesRef.current;
			const lastUserIdx = [...msgs].reverse().findIndex(m => m.role === 'user');
			if (lastUserIdx === -1) return;
			const idx = msgs.length - 1 - lastUserIdx;
			const history = msgs.slice(0, idx);
			const userText = msgs[idx].content;
			setMessagesRef.current(history);
			void sendMessageRef.current(userText, history);
		});
	}, [onRegisterRegenerate]);

	// Scroll handling
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
		};
		el.addEventListener('scroll', onScroll);
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!showScrollBtn) {
			messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		}
	}, [messages, showScrollBtn]);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
		setShowScrollBtn(false);
	}, []);

	const handleSend = useCallback(() => {
		if (isStreaming) return;
		const text = input.trim();
		setInput('');
		void sendMessage(text, messages); // empty string intentionally allowed — product decision
		setTimeout(() => inputRef.current?.focus(), 0);
	}, [input, isStreaming, sendMessage, messages]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend]
	);

	const handleSavePrompt = useCallback(
		(promptText: string) => {
			const trimmed = promptText.trim();
			if (!trimmed) return;
			const name = trimmed.length > 50 ? trimmed.substring(0, 50) : trimmed;
			CommitMutation(CreateSavedPromptMutation, {name, promptText: trimmed}, () => {
				setSavedPromptTexts(new Set([...savedPromptTexts, trimmed]));
				createToast({duration: 3000, message: formatMessage({id: 'nova.prompt.saved_toast'})});
			});
		},
		[formatMessage, savedPromptTexts, setSavedPromptTexts]
	);

	return (
		<PanelWrapper>
			<MessagesArea ref={scrollRef}>
				{messages.length === 0 && isProvisioning && (
					<ProvisioningState>{formatMessage({id: 'nova.provisioning'})}</ProvisioningState>
				)}
				{messages.length === 0 && !isProvisioning && <EmptyState onSuggestionClick={setInput} />}

				{messages.map((msg, idx) => (
					<MessageBubble
						key={msg.id}
						message={msg}
						isLastAssistant={msg.role === 'assistant' && idx === messages.length - 1 && isStreaming}
						history={history}
						onRespondAction={respondToAction}
						onResumeAction={actionId =>
							setMessages(prev =>
								prev.map(m => (m.actionPlan?.action_id === actionId ? {...m, actionPaused: false} : m))
							)
						}
					/>
				))}
				<div ref={messagesEndRef} />

				{showScrollBtn && (
					<ScrollToBottomBtn type="button" onClick={scrollToBottom}>
						↓
					</ScrollToBottomBtn>
				)}
			</MessagesArea>

			<DrawerFooter
				input={input}
				isStreaming={isStreaming}
				isHitlPending={isHitlPending}
				apiKey={apiKey}
				onInputChange={setInput}
				onSend={handleSend}
				onStop={abort}
				onKeyDown={handleKeyDown}
				inputRef={inputRef}
				onSavePrompt={handleSavePrompt}
			/>
		</PanelWrapper>
	);
};

export default ChatPanel;
